import { useMemo } from 'react';
import Hero from '@/pages/risk-management/components/Hero';
import SectionNav from '@/pages/risk-management/components/SectionNav';
import LearningPath from '@/pages/risk-management/components/LearningPath';
import TutorialLibrary from '@/pages/risk-management/components/TutorialLibrary';
import RiskCase from '@/pages/risk-management/components/RiskCase';
import RiskDiagram from '@/pages/risk-management/components/RiskDiagram';
import RiskMistakes from '@/pages/risk-management/components/RiskMistakes';
import { siteOrigin, useSeo } from '@/hooks/useSeo';
import { riskCategories, riskLearningStages } from '@/mocks/riskManagementPage';

export default function RiskManagement() {
  const jsonLd = useMemo(() => {
    const origin = siteOrigin();

    const seen = new Set<string>();
    const articles = riskCategories.flatMap((category) =>
      category.articles
        .filter((article) => {
          if (seen.has(article.slug)) return false;
          seen.add(article.slug);
          return true;
        })
        .map((article) => ({
          '@type': 'Article',
          headline: article.title,
          url: `${origin}/${article.slug}`,
        })),
    );

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'การบริหารความเสี่ยง Forex',
        description:
          'คลังบทความการบริหารความเสี่ยง Forex ภาษาไทย แบ่งเป็นหกหมวด ตั้งแต่พื้นฐานความเสี่ยง ขนาดการซื้อขาย Stop Loss Leverage และ Margin ไปจนถึง Drawdown และวินัยการเทรด',
        inLanguage: 'th-TH',
        url: `${origin}/risk-management/`,
        hasPart: articles,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LearningResource',
        name: 'ขั้นตอนพื้นฐานในการบริหารความเสี่ยง',
        description:
          'เส้นทางการเรียนรู้สี่ขั้นตอนสำหรับมือใหม่ ตั้งแต่การเข้าใจเงินทุนและความเสี่ยง การกำหนดขนาดการซื้อขาย Leverage และ Margin ไปจนถึงการสร้างวินัยในการเทรด',
        inLanguage: 'th-TH',
        educationalLevel: 'Beginner',
        learningResourceType: 'Course',
        isAccessibleForFree: true,
        url: `${origin}/risk-management/#learning-path`,
        teaches: riskLearningStages.map((stage) => stage.title),
      },
    ];
  }, []);

  useSeo({
    /* @replace:risk-title */
    title: 'การบริหารความเสี่ยง Forex: วิธีจัดการเงินทุนและ Stop Loss',
    description:
      /* @replace:risk-description */
      'เรียนรู้การบริหารความเสี่ยง Forex ตั้งแต่การจัดการขนาดการซื้อขาย การตั้ง Stop Loss การใช้ Leverage และ Margin ไปจนถึงการควบคุม Drawdown และวางแผนการเทรด',
    /* @replace:risk-keywords */
    keywords: 'การบริหารความเสี่ยง Forex, Stop Loss, Take Profit, Leverage, Margin, Drawdown, จัดการเงินทุน, Position Sizing',
    canonicalPath: '/risk-management/',
    jsonLd,
  });

  return (
    <div className="bg-background-50">
      <Hero />
      <SectionNav />
      <LearningPath />
      <TutorialLibrary />
      <RiskCase />
      <RiskDiagram />
      <RiskMistakes />
    </div>
  );
}